export class MusicPlayer {
  constructor(audioContext) {
    this.ctx =
      audioContext || new (window.AudioContext || window.webkitAudioContext)();

    // Output chain
    this.gainNode = this.ctx.createGain();
    this.gainNode.gain.value = 1;
    this.gainNode.connect(this.ctx.destination);

    // Current playback state
    this.currentSource = null;
    this.currentBuffer = null;
    this.isPlaying = false;
    this.loop = false;

    // Time tracking
    this.startTime = 0; // ctx.currentTime when playback started (minus offset)
    // Remember last position per buffer
    this.lastOffsets = new Map(); // AudioBuffer -> seconds
  }

  async createAudioBufferFromArrayBuffer(arrayBuffer) {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    }

    const buf =
      arrayBuffer instanceof ArrayBuffer ? arrayBuffer : arrayBuffer.buffer;

    return await this.ctx.decodeAudioData(buf.slice(0)); // slice to detach if needed
  }

  // 🔓 Call this once from a click/tap/keypress handler
  async unlock() {
    if (!this.ctx) return;
    if (this.ctx.state === "suspended") {
      await this.ctx.resume();
    }
  }

  /**
   * Play a given AudioBuffer.
   * @param {AudioBuffer} audioBuffer
   * @param {Object} options
   * @param {number} [options.volume=1.0]   Volume 0–1
   * @param {boolean} [options.resume=false] Resume from last position if available
   * @param {boolean} [options.loop=false]   Loop the song
   */
  async play(audioBuffer, options = {}) {
    const { volume = 1.0, resume = false, loop = false } = options;

    if (!audioBuffer) {
      console.warn("MusicPlayer.play called without an AudioBuffer");
      return;
    }

    // Stop whatever is currently playing (and store its position)
    this._storeCurrentPositionAndStop();

    this.currentBuffer = audioBuffer;
    this.loop = loop;

    // Determine starting offset
    let offset = 0;
    if (resume && this.lastOffsets.has(audioBuffer)) {
      offset = this.lastOffsets.get(audioBuffer) || 0;
      offset = Math.max(0, Math.min(offset, audioBuffer.duration));
    }

    // Create new source node
    const source = this.ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.loop = loop;
    source.connect(this.gainNode);

    this.setVolume(volume);

    this.currentSource = source;
    this.startTime = this.ctx.currentTime - offset;
    this.isPlaying = true;

    // When track ends, reset position (unless looping)
    source.onended = () => {
      if (!this.loop) {
        // reset to start
        this.lastOffsets.set(audioBuffer, 0);
        this.currentSource = null;
        this.currentBuffer = null;
        this.isPlaying = false;
      }
    };

    source.start(0, offset);
  }

  /**
   * Pause current song and remember position.
   */
  pause() {
    if (!this.isPlaying || !this.currentSource || !this.currentBuffer) return;

    const pos = this._getCurrentTimeInBuffer();
    this.lastOffsets.set(this.currentBuffer, pos);

    try {
      this.currentSource.stop();
    } catch (_) {}
    this.currentSource.disconnect();

    this.currentSource = null;
    this.isPlaying = false;
  }

  /**
   * Stop current song.
   * @param {boolean} [resetPosition=true] If true, next resume starts from 0
   */
  stop(resetPosition = true) {
    if (this.currentSource && this.currentBuffer) {
      const pos = this._getCurrentTimeInBuffer();
      this.lastOffsets.set(this.currentBuffer, pos);

      try {
        this.currentSource.stop();
      } catch (_) {}
      this.currentSource.disconnect();
    }

    if (resetPosition && this.currentBuffer) {
      this.lastOffsets.set(this.currentBuffer, 0);
    }

    this.currentSource = null;
    this.currentBuffer = null;
    this.isPlaying = false;
  }

  /**
   * Set volume (0–1).
   */
  setVolume(value) {
    const v = Math.max(0, Math.min(value, 1));
    this.gainNode.gain.value = v;
  }

  /**
   * Get current playback position in seconds for the current song.
   */
  getCurrentPosition() {
    if (!this.currentBuffer) return 0;
    if (this.isPlaying) {
      return this._getCurrentTimeInBuffer();
    }
    return this.lastOffsets.get(this.currentBuffer) || 0;
  }

  /**
   * Seek within the current song.
   * @param {number} seconds
   * @param {Object} options
   * @param {boolean} [options.playIfPaused=false] If true, start playing from new position
   */
  async seek(seconds, options = {}) {
    const { playIfPaused = false } = options;

    if (!this.currentBuffer) return;
    const clamped = Math.max(0, Math.min(seconds, this.currentBuffer.duration));
    this.lastOffsets.set(this.currentBuffer, clamped);

    const wasPlaying = this.isPlaying;

    // If currently playing, restart from new position
    if (wasPlaying || playIfPaused) {
      const buf = this.currentBuffer;
      const vol = this.gainNode.gain.value;
      const loop = this.loop;
      await this.play(buf, { volume: vol, resume: true, loop });
    }
  }

  /**
   * Internal: compute current playback time within buffer.
   */
  _getCurrentTimeInBuffer() {
    if (!this.currentBuffer) return 0;
    const elapsed = this.ctx.currentTime - this.startTime;
    return Math.max(0, Math.min(elapsed, this.currentBuffer.duration));
  }

  /**
   * Internal: store position of currently playing track and stop it.
   */
  _storeCurrentPositionAndStop() {
    if (!this.currentSource || !this.currentBuffer) return;

    const pos = this._getCurrentTimeInBuffer();
    this.lastOffsets.set(this.currentBuffer, pos);

    try {
      this.currentSource.stop();
    } catch (_) {}
    this.currentSource.disconnect();

    this.currentSource = null;
    this.isPlaying = false;
  }
}
